/**
 * admin_push_prompt_controller.js — identifier "admin-push-prompt".
 *
 * The admin PWA's own push prompt (templates/admin/_push_prompt.html). The
 * main app asks through a row in its bell. The admin app is a separate origin
 * with a separate subscription and a separate audience, so it asks for itself,
 * up front, with admin wording.
 *
 * Shows only when asking can work right now: Web Push supported, a VAPID key
 * configured, and notification permission never decided. Granted means the
 * push-registration controller is already subscribing silently. Denied cannot
 * be re-asked from a page (PWA Management explains how to unblock).
 *
 * Owns only the asking. "Turn on" dispatches chippie:push-enable synchronously,
 * which keeps the click gesture the browser requires for its permission
 * prompt, to the base.html push-registration mount. That controller already
 * owns requestPermission, the subscription and the device POST; a second copy
 * here would drift from it.
 *
 * Not nagging: "Not now" snoozes for 14 days. "Turn on" hides the strip for the
 * rest of the session, because a browser prompt the user closed without
 * choosing leaves permission undecided and would otherwise bring the strip
 * back on every Turbo navigation.
 */

import { Controller } from "@hotwired/stimulus";

const SNOOZE_KEY = "chippie:admin-push-prompt-snoozed-until";
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const ASKED_KEY = "chippie:admin-push-prompt-asked"; // sessionStorage

export default class extends Controller {
  static values = { vapidKey: String };

  connect() {
    this.element.hidden = !this.#promptable();
  }

  // data-action="admin-push-prompt#enable"
  enable(event) {
    if (event) event.preventDefault();
    sessionStorage.setItem(ASKED_KEY, "1");
    window.dispatchEvent(new CustomEvent("chippie:push-enable"));
    this.element.hidden = true;
  }

  // data-action="admin-push-prompt#dismiss"
  dismiss(event) {
    if (event) event.preventDefault();
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    this.element.hidden = true;
  }

  #promptable() {
    if (!this.vapidKeyValue) return false;
    if (
      !("Notification" in window) ||
      !("PushManager" in window) ||
      !("serviceWorker" in navigator)
    ) {
      return false;
    }
    if (Notification.permission !== "default") return false;
    if (sessionStorage.getItem(ASKED_KEY)) return false;
    return Date.now() >= Number(localStorage.getItem(SNOOZE_KEY) || 0);
  }
}
